import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { convertDoubleToCurrencyPTBR } from "../../../../../utils/ConverterUtils";
import { formatDate } from "../../../../../utils/DateUtils";
import { ApiError } from "../../../Common/ApiError";
import { successAlert, warnAlert } from "../../../alert/defaultAlert";
import ModalPageEvent from "../../../Common/Modal/ModalPageEvent";
import AsteriskRequired from "../../../Common/AsteriskRequired";
import InputText from "../../../Forms/Element/InputText";
import "./EmployeePaymentModal.css";

/**
 * Cancelamento de um pagamento do colaborador, com o motivo obrigatório.
 *
 * Usado pela tela Comissões e pela tela Pagamentos da equipe, para as duas pedirem e enviarem
 * o cancelamento do mesmo jeito. O servidor remove a conta a pagar e a saída do Caixa
 * Financeiro e guarda o pagamento como cancelado.
 *
 * @param {object} payment - O pagamento a cancelar (EmployeePaymentResource).
 * @param {function} onCanceled - Chamado depois do cancelamento, para a tela recarregar.
 */
const EmployeePaymentCancelModal = ({ show, setShow, payment, onCanceled }) => {

    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [isCanceling, setIsCanceling] = useState(false);

    // Cada abertura começa sem motivo: o de um cancelamento anterior não vale para outro.
    useEffect(() => {
        if (show) {
            setReason('');
        }
    }, [show]);

    /** Exige o motivo e envia; o servidor recusa sem motivo do mesmo jeito. */
    const confirm = () => {
        if (!reason.trim()) {
            warnAlert(t('cash_flow.employee_payment_cancel_reason_required'));
            return;
        }

        setIsCanceling(true);
        axiosInstance.patch(`${Api.cash_flow.employee_payment}/${payment.id}/cancel`, { reason: reason.trim() })
            .then(() => {
                setShow(false);
                successAlert(t('message.operation_successfully'), () => onCanceled?.());
            })
            .catch(error => ApiError(error))
            .finally(() => setIsCanceling(false));
    };

    return (
        <ModalPageEvent
            show={show}
            setShow={setShow}
            centered={true}
            title={t('cash_flow.employee_payment_cancel')}
            btnText={t('confirm')}
            btnOnClick={confirm}
            btnDisabled={isCanceling}
            child={payment && (
                <Row className="g-3">
                    <Col xs={12}>
                        <div className="employee-payment__header">
                            <strong>{payment.employee?.name} · {convertDoubleToCurrencyPTBR(payment.netValue)}</strong>
                            <span>
                                {t('cash_flow.employee_payment_period_range', {
                                    start: formatDate(payment.periodStart),
                                    end: formatDate(payment.periodEnd)
                                })}
                            </span>
                        </div>
                        <small className="text-muted d-block mt-2">{t('cash_flow.employee_payment_cancel_hint')}</small>
                    </Col>
                    <Col xs={12}>
                        <Form.Group>
                            <Form.Label>{t('reason')} <AsteriskRequired /></Form.Label>
                            <InputText
                                name="cancelReason"
                                maxLength={255}
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            )}
        />
    );
};

export default EmployeePaymentCancelModal;
